export type Volume = {
  number: string
  title: string
  subtitle: string
  badge?: string
  problem: string[]
  extraList?: { label: string; items: string[] }
  listLabel: string
  bullets: string[]
  highlight?: { label: string; items: string[] }
  flow?: { label?: string; steps: string[] }
  result: string[]
}

export const volumes: Volume[] = [
  {
    number: 'VOLUME 1',
    title: 'Salsa Foundations, Timing, Footwork & Shines',
    subtitle: 'Build strong Salsa fundamentals before adding complexity',
    problem: [
      'Strong Salsa students are not built by memorizing endless combinations.',
      'They are built through timing, weight transfer, coordination, clean footwork and progressive technical development.',
      'This volume gives you a visual teaching system for building those foundations step by step.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Salsa timing and rhythm fundamentals',
      'Weight transfer',
      'Basic step mechanics',
      'Body positioning',
      'Essential footwork',
      'Coordination',
      'Direction changes',
      'Shines',
      'Technical progressions',
      'Common errors',
      'Instructor corrections',
      'Beginner-to-advanced development',
    ],
    result: [
      'Your students build cleaner foundations, stronger timing and better coordination before moving into more complex material.',
    ],
  },
  {
    number: 'VOLUME 2',
    title: 'Bachata Foundations, Body Movement, Footwork & Styling',
    subtitle:
      'Teach Bachata technique progressively instead of introducing random movements',
    problem: [
      'Bachata requires much more than knowing the basic step.',
      'Students need control, timing, weight transfer, body movement, coordination and progressive development.',
      'This volume helps you teach those elements through a clear visual structure.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Bachata timing',
      'Basic step mechanics',
      'Weight transfer',
      'Body movement fundamentals',
      'Isolations',
      'Coordination',
      'Direction changes',
      'Footwork',
      'Styling foundations',
      'Technical progressions',
      'Common mistakes and corrections',
    ],
    result: [
      'Your students stop simply copying movements and begin developing the control and technique behind them.',
    ],
  },
  {
    number: 'VOLUME 3',
    title: 'Partnerwork, Connection, Leading, Following & Turns',
    subtitle: 'A 100% visual system for teaching partnerwork with clarity',
    problem: [
      'Partnerwork should not depend on pulling, guessing or memorizing combinations.',
      'This volume visually breaks down connection, frame, leading, following, turns, pathways and transitions, allowing you to see exactly how each movement should be taught.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Open and closed partner positions',
      'Frame',
      'Connection pressure',
      'Weight transfer',
      'Leading and following principles',
      'Partner spacing',
      'Salsa cross-body pathways',
      'Right and left turns',
      'Inside and outside turns',
      'Hand positions',
      'Hand changes',
      'Wraps',
      'Transitions',
      'Bachata partnerwork',
      'Common partnerwork errors',
      'Turn troubleshooting',
      'Connection troubleshooting',
      'Beginner-to-advanced progressions',
    ],
    highlight: {
      label: 'The visual advantage — see:',
      items: [
        'Where the leader stands.',
        'Where the follower stands.',
        'Where the hands connect.',
        'Who initiates.',
        'Who responds.',
        'Where each dancer moves.',
        'How the turn progresses.',
        'How to correct the mistake.',
      ],
    },
    result: [
      'Your students stop memorizing partner patterns and begin understanding how partnerwork actually works.',
    ],
  },
  {
    number: 'VOLUME 4',
    title: 'Musicality, Combinations & Social Dance Development',
    subtitle:
      'Help students stop counting movements and start dancing with the music',
    problem: [
      'Knowing combinations is not the same as understanding musicality.',
      'This volume helps instructors connect technique to rhythm, phrasing, interpretation and practical social dance application.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Musical structure',
      'Rhythm recognition',
      'Timing',
      'Accents',
      'Phrasing',
      'Salsa musicality',
      'Bachata musicality',
      'Combination building',
      'Transition quality',
      'Musical interpretation',
      'Social dance flow',
      'Progressive combinations',
      'Practical class applications',
    ],
    result: [
      'Your students begin connecting movement to music instead of simply executing memorized patterns.',
    ],
  },
  {
    number: 'VOLUME 5',
    title:
      'Instructor Method, Corrections, Class Structure & Student Progression',
    subtitle:
      'Knowing how to dance is one skill. Knowing how to teach it is another.',
    problem: [
      'This volume is built specifically around the instructor.',
      'It helps you organize classes, communicate movements more clearly, identify mistakes, adapt difficulty and create progressions your students can actually follow.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Teaching methodology',
      'Class structure',
      'Demonstration strategies',
      'Instructor cueing',
      'Correction systems',
      'Common-error identification',
      'Beginner class development',
      'Intermediate progression',
      'Mixed-level instruction',
      'Student observation',
      'Skill progression',
      'Difficulty adaptation',
      'Teaching consistency',
    ],
    result: [
      'You stop relying only on improvisation and begin teaching through a repeatable professional method.',
    ],
  },
  {
    number: 'VOLUME 6',
    title: '150 Ready-to-Teach Salsa & Bachata Classes',
    subtitle: 'Stop building every class from scratch',
    badge: 'Most popular',
    problem: [
      'This is the practical heart of the complete 6-in-1 system.',
      'You receive 150 structured classes ready to teach, covering the essential areas developed throughout the other five volumes.',
    ],
    extraList: {
      label: 'Choose according to:',
      items: [
        'dance style',
        'student level',
        'technical objective',
        'class focus',
        'partnerwork goal',
        'musicality goal',
        'progression stage',
      ],
    },
    listLabel: 'Classes cover:',
    bullets: [
      'Salsa foundations',
      'Bachata foundations',
      'Timing',
      'Footwork',
      'Shines',
      'Body movement',
      'Partnerwork',
      'Connection',
      'Leading and following',
      'Turns',
      'Hand changes',
      'Musicality',
      'Combinations',
      'Social dance development',
      'Technique',
      'Corrections',
      'Beginner development',
      'Intermediate development',
      'Advanced progressions',
      'Mixed-skill classes',
    ],
    flow: { steps: ['Open', 'Choose', 'Teach'] },
    result: [
      'Instead of asking yourself: “What am I teaching tomorrow?”',
      'You open the system and already have a structured starting point.',
    ],
  },
]
